import { useCallback, useMemo } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import {
  TextInput,
  Select,
  TextInputWithPrefix,
  Button,
} from "../ui";
import { formatCurrency } from "../../_utils/pricing";
import "./_tax-editor.scss";

let idCounter = 0;
const generateId = () => `tax-${Date.now()}-${idCounter++}`;

const TAX_TYPE_OPTIONS = [
  { label: "Flat", value: "Flat" },
  { label: "Percent", value: "Percent" },
];

const columnHelper = createColumnHelper();

/**
 * Editable table of tax rows. Same raw-state + functional-updater pattern
 * as LineItemsEditor (see that file's docstring) for stable handler
 * identity. Rows aren't split into their own React.memo'd component the
 * way line items are - tax lists are typically 1-3 rows, so the extra
 * indirection isn't worth it here (targeted memoization, not blanket).
 *
 * Rendered via TanStack Table (column defs + flexRender) rather than a
 * hand-mapped `.map()`, matching the other proposal-wizard tables - purely
 * a rendering-mechanism change, same markup/classNames/styling as before.
 */
const TaxEditor = ({ taxes, setRawTaxes, currencySign }) => {
  const handleAdd = useCallback(() => {
    setRawTaxes((prev) => [
      ...prev,
      { id: generateId(), taxLabel: "", taxType: "Percent", taxValue: "" },
    ]);
  }, [setRawTaxes]);

  const handleChange = useCallback(
    (id, patch) => {
      setRawTaxes((prev) =>
        prev.map((tax) => (tax.id === id ? { ...tax, ...patch } : tax)),
      );
    },
    [setRawTaxes],
  );

  const handleRemove = useCallback(
    (id) => {
      setRawTaxes((prev) => prev.filter((tax) => tax.id !== id));
    },
    [setRawTaxes],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("taxLabel", {
        header: "Tax label",
        cell: (info) => (
          <TextInput
            compact
            placeholder="Tax label"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleChange(info.row.original.id, { taxLabel: value })
            }
          />
        ),
      }),
      columnHelper.accessor("taxType", {
        header: "Type",
        cell: (info) => (
          <Select
            compact
            options={TAX_TYPE_OPTIONS}
            value={info.getValue() ?? "Percent"}
            onChange={(value) =>
              handleChange(info.row.original.id, { taxType: value })
            }
          />
        ),
      }),
      columnHelper.accessor("taxValue", {
        header: "Value",
        cell: (info) => (
          <TextInputWithPrefix
            compact
            prefix={
              info.row.original.taxType === "Percent" ? "%" : currencySign
            }
            placeholder="0"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleChange(info.row.original.id, { taxValue: value })
            }
          />
        ),
      }),
      columnHelper.accessor("totalTax", {
        header: "Total",
        meta: { className: "tax-editor__col-total" },
        cell: (info) => formatCurrency(info.getValue(), currencySign),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) => (
          <Button
            variant="ghost-danger"
            size="md"
            iconOnly
            icon={<Trash2 size={16} />}
            onClick={() => handleRemove(info.row.original.id)}
            aria-label={`Remove ${info.row.original.taxLabel || "tax"}`}
          />
        ),
      }),
    ],
    [currencySign, handleChange, handleRemove],
  );

  const table = useReactTable({
    data: taxes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="tax-editor">
      <div className="tax-editor__header">
        <h4 className="tax-editor__title">Taxes</h4>
        <Button variant="ghost" size="md" onClick={handleAdd}>
          + Add tax
        </Button>
      </div>

      {taxes.length === 0 ? (
        <p className="tax-editor__empty">No taxes added yet.</p>
      ) : (
        <div className="tax-editor__scroll">
          <table className="tax-editor__rows">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                      aria-hidden={header.id === "actions" || undefined}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaxEditor;
